import React, { useMemo } from 'react';
import { Eye, Pencil, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import { Report } from '@/pages/reports/schemas';
import { useData } from '@/contexts/data';
import { canResendReport, canStartReportSend, normalizeReportStatus } from '@/lib/reportStatus';
import { SendStatusBadge } from '@/pages/reports/list/components/SendStatusBadge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ReportHeaderProps {
  report: Report;
  reportId: string;
  onEdit: () => void;
  onStartSend: () => void;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  report,
  reportId,
  onEdit,
  onStartSend,
}) => {
  const { partners } = useData();
  const reportDate = format(report.updatedAt, 'dd/MM/yyyy', { locale: ptBR });
  const reportStatus = normalizeReportStatus(report.status);
  const canStartSend = canStartReportSend(reportStatus);
  const canResend = canResendReport(reportStatus);

  const partnerLabel = useMemo(() => {
    if (report.partner_name?.trim()) return report.partner_name.trim();
    if (report.partner_id) {
      const partner = partners.find((item) => item.id === report.partner_id);
      if (partner?.name) return partner.name;
    }
    return 'Geral';
  }, [partners, report.partner_id, report.partner_name]);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{report.title}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Report {reportDate}</span>
            <Badge className="bg-black text-white hover:bg-black border-transparent py-[2px] px-2.5 rounded-full">
              {partnerLabel}
            </Badge>
            <SendStatusBadge status={reportStatus} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <PermissionGuard action="edit_reports">
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              className="flex items-center gap-2 h-9 px-4"
            >
              <Pencil className="w-4 h-4" />
              Editar
            </Button>
          </PermissionGuard>
          <Button
            size="sm"
            variant="outline"
            asChild
            className="flex items-center gap-2 h-9 px-4"
          >
            <a
              href={`/app/reports/${reportId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Visualizar
            </a>
          </Button>
          <PermissionGuard action="send_reports">
            {canStartSend ? (
              <Button
                size="sm"
                variant="dark"
                onClick={onStartSend}
                className="flex items-center gap-2 h-9 px-4"
              >
                <Send className="w-4 h-4" />
                Enviar report
              </Button>
            ) : null}
            {canResend ? (
              <Button
                size="sm"
                variant="dark"
                onClick={onStartSend}
                className="flex items-center gap-2 h-9 px-4"
              >
                <Send className="w-4 h-4" />
                Reenviar report
              </Button>
            ) : null}
          </PermissionGuard>
        </div>
      </div>
      <hr className="border-gray-200 mt-6 mb-6" />
    </>
  );
};
